// Global media security hook
import { useEffect } from 'react';

export const useMediaSecurity = () => {
  useEffect(() => {
    // Disable right-click globally
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Check if the target is a media element or has media-related classes
      const isMediaElement = 
        target.tagName === 'VIDEO' ||
        target.tagName === 'AUDIO' ||
        target.tagName === 'IMG' ||
        target.tagName === 'SOURCE' ||
        target.closest('video') ||
        target.closest('audio') ||
        target.closest('img') ||
        target.classList.contains('secure-media') ||
        target.closest('.secure-media');

      if (isMediaElement) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Disable keyboard shortcuts that could be used to download/save media
    const handleKeyDown = (e: KeyboardEvent) => {
      // Common download/save shortcuts
      if (
        (e.ctrlKey && e.key === 's') || // Ctrl+S (Save)
        (e.ctrlKey && e.shiftKey && e.key === 'S') || // Ctrl+Shift+S (Save As)
        (e.ctrlKey && e.key === 'u') || // Ctrl+U (View Source)
        (e.ctrlKey && e.shiftKey && e.key === 'I') || // Ctrl+Shift+I (Developer Tools)
        (e.ctrlKey && e.shiftKey && e.key === 'C') || // Ctrl+Shift+C (Inspect Element)
        (e.ctrlKey && e.shiftKey && e.key === 'J') || // Ctrl+Shift+J (Console)
        e.key === 'F12' // Developer Tools
      ) {
        // Check if focus is on or near media elements
        const activeElement = document.activeElement;
        const focusedOnMedia = 
          activeElement?.tagName === 'VIDEO' ||
          activeElement?.tagName === 'AUDIO' ||
          activeElement?.tagName === 'IMG' ||
          activeElement?.closest('video') ||
          activeElement?.closest('audio') ||
          activeElement?.closest('img') ||
          document.querySelector('video:hover') ||
          document.querySelector('audio:hover') ||
          document.querySelector('img:hover');

        if (focusedOnMedia) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    };

    // Disable drag and drop for media elements
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement;
      
      if (
        target.tagName === 'VIDEO' ||
        target.tagName === 'AUDIO' ||
        target.tagName === 'IMG'
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection on media elements
    const handleSelectStart = (e: Event) => {
      const target = e.target as HTMLElement;
      
      if (
        target.tagName === 'VIDEO' ||
        target.tagName === 'AUDIO' ||
        target.tagName === 'IMG'
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    document.addEventListener('dragstart', handleDragStart, { capture: true });
    document.addEventListener('selectstart', handleSelectStart, { capture: true });

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      document.removeEventListener('dragstart', handleDragStart, { capture: true });
      document.removeEventListener('selectstart', handleSelectStart, { capture: true });
    };
  }, []);
};

// CSS styles to be added to media elements for security
export const getSecureMediaStyles = (): React.CSSProperties => ({
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  WebkitTouchCallout: 'none',
  WebkitUserDrag: 'none',
  KhtmlUserDrag: 'none',
  MozUserDrag: 'none',
  OUserDrag: 'none',
  pointerEvents: 'auto', // Allow normal interaction but prevent drag
});

export default useMediaSecurity;
